import {useState} from 'react';

import {useQuery} from 'react-query';

import useAuth from 'auth/provider/useAuth';
import UserAvatar from 'components/user/UserAvatar';
import {TextAreaField} from 'components_sb/input';
import {Card} from 'components_sb/layout';
import {Paragraph} from 'components_sb/typography';
import {API_URL} from 'globals/app-globals';
import useLocalUserSettings from 'hooks/useLocalUserSettings';
import ChatMessage from 'models/ChatMessage';
import Conversation from 'models/Conversation';
import {AccountRole} from 'models/users/User';
import {Page} from 'router/components';
import useRouter from 'router/hooks/useRouter';
import {usePusherService} from 'services/PusherService';

const NewChatPage = () => {
  const router = useRouter();

  const {fetchChannels} = usePusherService();

  const [selectedContact, setSelectedContact] = useState(null);
  const [message, setMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const {activeAccountRole} = useLocalUserSettings();
  const {authCookies} = useAuth();
  const {data, isLoading} = useQuery(
    `${activeAccountRole}-contacts`,
    async () => {
      const response = await fetch(API_URL + '/contacts.json', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-USER-TOKEN': authCookies.token,
          'X-USER-EMAIL': authCookies.userEmail,
          'X-ACCOUNT-TYPE': activeAccountRole,
        },
      });

      const json = await response.json();
      return json.contacts;
    },
  );

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    setIsSaving(true);
    const convo = new Conversation({receiverId: selectedContact.id});
    const msg = new ChatMessage({content: message});
    convo.chat_messages = [msg] as any;

    const result = await convo.save({with: 'chatMessages'});
    if (result) {
      setMessage('');
      await fetchChannels();
      setIsSaving(false);
      const key = 'Conversation-' + convo.id.toString();
      router.navigate(`/chat/${key}`);
    } else {
      console.log(convo.errors);
    }
  };

  return (
    <Page title="New Conversation" loading={isLoading}>
      {() => (
        <Card title="New Conversation" className="mt-2">
          {data.length === 0 ? (
            <>
              <Paragraph>You do not have any contacts on Keyhook.</Paragraph>
              <Paragraph>
                {activeAccountRole === AccountRole.Landlord &&
                  'Once your tenants are on Keyhook, you can choose to chat with them from here.'}
                {activeAccountRole === AccountRole.Renter &&
                  'Once you are part of a tenancy, you will be able to message your landlord or fellow tenants from here.'}
              </Paragraph>
            </>
          ) : (
            <Paragraph>
              Select a contact from below to start a new conversation with them.
            </Paragraph>
          )}

          <strong className="tw-block font-semibold text-xl mt-2">
            Contacts
          </strong>
          {data.map((contact: any) => (
            <div
              className={`flex justify-start items-center py-2 border-b-2 border-gray-100 cursor-pointer ${
                selectedContact &&
                selectedContact.id === contact.id &&
                'bg-gray-100'
              }`}
              onClick={() => setSelectedContact(contact)}
              key={contact.id}>
              <UserAvatar user={contact} size="10" />
              <strong
                className={`tw-block ml-2 ${
                  selectedContact &&
                  selectedContact.id === contact.id &&
                  'text-primary'
                }`}>
                {contact.name}
              </strong>
            </div>
          ))}

          <form onSubmit={handleSubmit} className="mt-2">
            <TextAreaField
              placeholder="Type your message here..."
              name="message"
              label="Message"
              mode="manual"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onClick={() => 'noop'}
            />
            <button
              className="btn btn-neutral btn-block mt-4 w-full"
              disabled={isSaving || !selectedContact || message.length === 0}
              type="submit">
              Send
            </button>
          </form>
        </Card>
      )}
    </Page>
  );
};

export default NewChatPage;
